import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import CartContext from "../../context/CartContext";
import AuthContext from "../../context/authContext";
import logo from "../../img/logo.png";

const formatPrice = (price) =>
  Number(price || 0).toLocaleString("pt-AO", {
    style: "currency",
    currency: "AOA",
    minimumFractionDigits: 2,
  });

// ===== Helpers (mesma lógica do CriarFatura) =====
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const aplicar25Porcento = (preco) => round2(Number(preco || 0) * 1.25);

const ConfirmarEncomenda = () => {
  const { cart, clearCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const proformaRef = useRef();

  const [endereco, setEndereco] = useState(null);
  const {
    cliente,
    enderecoSelecionado,
    pontoReferencia,
    instrucoesEntrega,
    metodoPagamento,
  } = location.state || {};

  useEffect(() => {
    if (!user?.token || enderecoSelecionado) return;

    const fetchEndereco = async () => {
      try {
        const response = await axios.get("https://api.nwayami.com/api/enderecos/", {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (response.data.length > 0) setEndereco(response.data[0]);
      } catch (error) {
        console.error("Erro ao buscar endereço:", error);
      }
    };

    fetchEndereco();
  }, [user, enderecoSelecionado]);

  // ===== Deriva linhas e total já com +25% =====
  const linhas = useMemo(() => {
    return cart.map((item, i) => {
      const quantidade = item.quantity || 1;
      const precoBase = Number(item.preco_com_iva || 0);
      const precoUnitario25 = aplicar25Porcento(precoBase); // preço unitário já com +25%
      const totalLinha = round2(precoUnitario25 * quantidade);
      return {
        ...item,
        idx: i,
        quantidade,
        precoBase,
        precoUnitario25,
        totalLinha,
      };
    });
  }, [cart]);

  const totalGeral = useMemo(
    () => round2(linhas.reduce((acc, l) => acc + l.totalLinha, 0)),
    [linhas]
  );

  const gerarPDF = async () => {
    const input = proformaRef.current;
    if (!input) return;

    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = 210;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Proforma_${new Date().toLocaleDateString("pt-PT")}.pdf`);
  };

  const handleConfirmarPedido = async () => {
    const enderecoFinal = enderecoSelecionado || endereco;
    if (!user?.token) return alert("Faça login para continuar.");
    if (!enderecoFinal?.id) return alert("Informe um endereço válido.");

    // Envia o preço já com +25%, igual ao CriarFatura
    const pedidoData = {
      endereco: enderecoFinal.id,
      pontoReferencia,
      instrucoesEntrega,
      metodoPagamento,
      cart: linhas.map((l) => ({
        id: l.id,
        // mantém o nome do campo que seu backend espera,
        // mas agora com o valor já acrescido
        preco_com_iva: l.precoUnitario25,
        quantity: l.quantidade,
      })),
    };

    try {
      const response = await axios.post(
        "https://api.nwayami.com/api/pedidos/criar/",
        pedidoData,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      const emailStatus = response.data.email;
      if (emailStatus === "enviado") {
        alert("✅ Pedido confirmado e e-mail enviado com sucesso!");
      } else {
        alert(`⚠️ Pedido confirmado, mas ocorreu um erro ao enviar o e-mail: ${emailStatus}`);
      }

      clearCart();
      navigate("/");
    } catch (error) {
      console.error("Erro ao confirmar pedido:", error);
      alert("❌ Erro ao confirmar pedido. Tente novamente.");
    }
  };

  const enderecoParaExibir = enderecoSelecionado || endereco;

  if (!cliente || !enderecoParaExibir) {
    return (
      <div className="container mt-5 text-center">
        <h4>⚠️ Nenhuma informação disponível.</h4>
        <p>Retorne à página anterior para preencher os dados da encomenda.</p>
      </div>
    );
  }

  return (
    <div className="container my-2">
      <div ref={proformaRef} className="bg-white p-4 shadow" style={{marginTop:"100px"}}>
     

        <table className="table table-bordered mt-4" >
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição</th>
              <th>Preço</th>
              <th>UND</th>
              <th>Qtd</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id ?? l.idx}>
                <td>1.{l.idx + 1}</td>
                <td>{l.descricao}</td>
                <td>{formatPrice(l.precoUnitario25)}</td>
                <td>{l.unidade || "-"}</td>
                <td>{l.quantidade}</td>
                <td>{formatPrice(l.totalLinha)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h5 className="text-end">Total: {formatPrice(totalGeral)}</h5>
      </div>

      <div className="text-center mt-3">
        <button className="btn btn-success mx-2" onClick={handleConfirmarPedido}>
          Confirmar Pedido
        </button>
       
      </div>
    </div>
  );
};

export default ConfirmarEncomenda;
